import { useEffect, useState } from 'react';
import Slider from 'rc-slider';
import './App.css';
import classNames from 'classnames';
import 'rc-slider/assets/index.css';
import Option from './Option';
import { data } from './data';
import db from './firebase';
import { collection, addDoc } from 'firebase/firestore';

const emojiBar = [
  '😡',
  '😖',
  '😔',
  '😒',
  '😑',
  '😶',
  '🙂',
  '😀',
  '😁',
  '😍',
  '😘',
];

type Word = {
  word: string;
  lang: string;
};

function shuffle(array: Word[]) {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle...
  while (currentIndex != 0) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

type Input = {
  word: string;
  rating: number;
};

function App() {
  const [screen, setScreen] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sliderVal, setSliderVal] = useState<number | null>(null);

  const [hindi, setHindi] = useState(false);
  const [bengali, setBengali] = useState(false);
  const [malayalam, setMalayalam] = useState(false);
  const [tamil, setTamil] = useState(false);
  const [nsfw, setNSFW] = useState(false);

  const [words, setWords] = useState<{ word: string; lang: string }[]>([]);
  const [wordIndex, setWordIndex] = useState(0);
  const [wordPicked, setWordPicked] = useState(false);

  const [inputs, setInputs] = useState<Input[]>([]);

  const responsesRef = collection(db, 'responses');

  const ahead = () => {
    setIsLoading(true);
    setTimeout(() => {
      setScreen(screen + 1);
      setSliderVal(null);
      setIsLoading(false);
    }, 600);
  };

  const langpickahead = () => {
    let newWords = [] as Word[];
    if (hindi) {
      newWords = [
        ...newWords,
        ...data.hindi.map((word) => ({ word, lang: 'hindi' })),
      ];
      if (nsfw) {
        newWords = [
          ...newWords,
          ...data.expl_hindi.map((word) => ({ word, lang: 'hindi' })),
        ];
      }
    }
    if (bengali) {
      newWords = [
        ...newWords,
        ...data.bengali.map((word) => ({ word, lang: 'bengali' })),
      ];
      if (nsfw) {
        newWords = [
          ...newWords,
          ...data.expl_bengali.map((word) => ({ word, lang: 'bengali' })),
        ];
      }
    }
    if (malayalam) {
      newWords = [
        ...newWords,
        ...data.malayalam.map((word) => ({ word, lang: 'malayalam' })),
      ];
    }
    if (tamil) {
      newWords = [
        ...newWords,
        ...data.tamil.map((word) => ({ word, lang: 'tamil' })),
      ];
    }
    shuffle(newWords);
    setWords(newWords);
    ahead();
  };

  const saveEverything = (inps?: Input[]) => {
    const myinp = inps || inputs;
    const obj = Object.fromEntries(
      myinp.map(({ word, rating }) => [word, rating])
    );
    addDoc(responsesRef, obj);
    localStorage.setItem('expressions', 'yes');
    localStorage.setItem('exp-res', JSON.stringify(myinp));
  };

  useEffect(() => {
    if (localStorage.getItem('expressions') === 'yes') {
      const expRes = localStorage.getItem('exp-res');
      if (expRes) {
        setInputs(JSON.parse(expRes) as Input[]);
      }
      setScreen(4);
    }
  }, []);

  const nextWord = () => {
    if (wordIndex < words.length - 1) {
      setInputs([
        ...inputs,
        {
          word: words[wordIndex].word,
          rating: sliderVal,
        } as Input,
      ]);
      setWordIndex(wordIndex + 1);
      setWordPicked(false);
      setSliderVal(null);
    } else {
      const newObj = [
        ...inputs,
        { word: words[wordIndex].word, rating: sliderVal },
      ];
      saveEverything(newObj as Input[]);
      ahead();
    }
  };

  const skipWord = () => {
    if (wordIndex < words.length - 1) {
      setWordIndex(wordIndex + 1);
      setWordPicked(false);
      setSliderVal(null);
    } else {
      saveEverything();
      ahead();
    }
  };

  return (
    <div className={classNames('App bg-white', isLoading && 'loading')}>
      {screen === 0 && (
        <div className='heading'>
          <h1 className='title'>Jhakkas</h1>
          <p>
            An experiment to quantify the sentiment of different Indian
            expressions
          </p>
          <button
            onClick={ahead}
            className='px-4 py-2 bg-gray-600 hover:bg-gray-800 text-white mt-12 rounded-md'
          >
            Let's Go
          </button>
          <div className='absolute bottom-0 illus'>
            <img
              alt='mast'
              src='https://c.tenor.com/pwFIRRljBjAAAAAi/mast-%E0%A4%AC%E0%A4%B9%E0%A5%81%E0%A4%A4.gif'
            />
          </div>
        </div>
      )}
      {screen === 1 && (
        <div className='p-4 text-center pt-12'>
          <div>Which language(s) are you comfortable in?</div>
          <div className='mt-12 flex flex-col items-center'>
            <Option onPicked={() => setHindi(!hindi)} isPicked={hindi}>
              Hindi
            </Option>
            <Option onPicked={() => setBengali(!bengali)} isPicked={bengali}>
              Bengali
            </Option>
            <Option onPicked={() => setTamil(!tamil)} isPicked={tamil}>
              Tamil
            </Option>
            <Option
              onPicked={() => setMalayalam(!malayalam)}
              isPicked={malayalam}
            >
              Malayalam
            </Option>
          </div>
          <div className='mt-6 items-center flex flex-col'>
            <p className='mb-4'>Should we include NSFW words?</p>
            <Option onPicked={() => setNSFW(!nsfw)} isPicked={nsfw}>
              NSFW
            </Option>
          </div>
          <div className='mt-6 items-center flex flex-col'>
            <button
              onClick={langpickahead}
              className='px-4 py-2 bg-gray-600 hover:bg-gray-800 text-white mt-12 rounded-md'
            >
              Continue
            </button>
          </div>
        </div>
      )}
      {screen === 2 && (
        <div className='p-4 positi text-center'>
          <p>
            <b>This is the positivity slider.</b>
          </p>
          <p>Use it to convey the degree of positivity a phrase conveys</p>
          <p className='mt-4'>
            <i>0 for extremely negative, 10 for extremely positive</i>
          </p>
          <div className='emojocontrol px-4'>
            <Slider
              onChange={(e) => {
                setSliderVal(Number(e / 10));
              }}
              step={10}
            />
            <div className='emojo'>
              {sliderVal !== null && (
                <div
                  style={{
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div>{sliderVal}</div>
                  <div>{emojiBar[sliderVal]}</div>
                </div>
              )}
            </div>
          </div>
          {sliderVal !== null && (
            <button
              onClick={ahead}
              className='px-4 py-2 bg-gray-600 hover:bg-gray-800 text-white mt-4 rounded-md'
            >
              Proceed
            </button>
          )}
        </div>
      )}
      {screen === 3 && (
        <div className='p-4'>
          <div
            className='text-5xl font-bold mb-4 pt-12'
            style={{ textAlign: 'center' }}
          >
            {words[wordIndex].word}
          </div>
          <p className='text-center text-sm mb-8'>
            Language: {words[wordIndex].lang}
          </p>
          <div
            className='text-sm italic text-gray-500'
            style={{ textAlign: 'center' }}
          >
            What degree of positivity from 0 to 10 this expression evokes in
            you?
          </div>
          <div className='emojocontrol px-4'>
            <Slider
              onChange={(e) => {
                setSliderVal(Number(e / 10));
                setWordPicked(true);
              }}
              value={sliderVal ? sliderVal * 10 : 0}
              step={10}
            />
            <div className='emojo'>
              <div
                style={{
                  fontFamily: 'monospace',
                  textAlign: 'center',
                  justifyContent: 'center',
                }}
              >
                <div>{sliderVal || 0}</div>
                <div>{emojiBar[sliderVal || 0]}</div>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={nextWord}
              disabled={!wordPicked}
              className={classNames(
                wordPicked ? 'bg-gray-600 hover:bg-gray-800' : 'bg-gray-200',
                'px-4 py-2 text-white mt-4 rounded-md'
              )}
            >
              Next
            </button>
            <div className='text-center'>
              <button
                onClick={skipWord}
                className={classNames(
                  'bg-gray-200',
                  'px-4 py-2 text-black mt-4 rounded-md'
                )}
              >
                Skip
              </button>
            </div>
            {inputs.length > 8 && (
              <div
                className='underline mt-12 text-sm'
                onClick={() => {
                  saveEverything();
                  ahead();
                }}
              >
                End Test
              </div>
            )}
          </div>
        </div>
      )}
      {screen === 4 && (
        <div className='p-4'>
          <div className='text-3xl font-bold mb-4 pt-12'>
            Your responses were recorded! Thank you for participating!
          </div>
          <div>
            {inputs
              .sort((a, b) => b.rating - a.rating)
              .map((inp) => (
                <div className='flex mb-2 justify-between'>
                  <div>{inp.word}</div>
                  <div className='font-bold'>{inp.rating}</div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
